import { flushQueue } from '~core/util/flushQueue'
import type {
  EnvironmentType,
  LogData,
  LogLevels,
  OutputInterface,
} from '../types'
import { performHttpRequest } from '~core/Network'
import { reduxStore } from 'components/ReduxAppWrapper'
import {
  createAnalyticsPayload,
  AnalyticsPayload,
} from '~core/Analytics/createAnalyticsPayload'
import { SdkConfiguration } from '~core/SdkConfiguration/types'
import { trackException } from 'Tracker'
import { formatError } from '~utils/onfidoApi'
import { ParsedError } from '~types/api'

let token: string | undefined
let url: string | undefined
let sdkConfiguration: SdkConfiguration | undefined

type LoggerProperties = {
  event_type: 'log'
  log_labels: string[]
  log_level: string
  log_metadata: Record<string, unknown>
}

export class NetworkOutput implements OutputInterface {
  private queue: LogData[] = []

  constructor() {
    this.listenToReduxStore()
  }

  /* 
    Note: We need to wait for the token, url & sdkConfiguration 
    to be available in the store before sending any network requests
  */
  private listenToReduxStore() {
    reduxStore.subscribe(() => {
      const store = reduxStore.getState()

      token = store.globals.token
      url = store.globals.urls.onfido_api_url
      sdkConfiguration = store.globals.sdkConfiguration

      if (token && url && sdkConfiguration && this.queue.length) {
        flushQueue<LogData>(this.queue, 1, (chunks) => this.write(chunks[0]))
        this.queue = []
      }
    })
  }

  private sendToBackend = (events: AnalyticsPayload<LoggerProperties>[]) => {
    performHttpRequest(
      {
        method: 'POST',
        contentType: 'application/json',
        endpoint: `${url}/v4/sdk/logger`,
        token: `Bearer ${token}`,
        payload: JSON.stringify({ events }),
      },
      this.onNetworkSucces,
      (request) => formatError(request, this.onNetworkError)
    )
  }

  private createLogPayload(data: LogData): AnalyticsPayload<LoggerProperties> {
    return createAnalyticsPayload<LoggerProperties>({
      event: 'log',
      event_time: data.timestamp,
      properties: {
        event_type: 'log',
        log_labels: data.labels,
        log_level: data.level,
        log_metadata: {
          message: data.message,
          metadata: data.metadata,
          file: data.file,
          method: data.method,
          line: data.line,
        },
      },
    })
  }

  private onNetworkSucces() {}
  private onNetworkError = (error: ParsedError) => {
    trackException('Could not send logger payload to backend', {
      data: { status: error.status },
    })
  }

  public write = (data: LogData, environment?: EnvironmentType) => {
    if (environment !== 'production') {
      return
    }

    // In case sdkConfiguration is not loaded: add any logs to the waitQueue.
    // they will be filtered when the sdkConfiguration is available.
    if (!sdkConfiguration) {
      this.queue.push(data)
      return
    }

    const enabled = sdkConfiguration?.sdk_features?.logger?.enabled || false
    const enabledLevels = sdkConfiguration?.sdk_features?.logger?.levels || []

    if (enabled && enabledLevels.indexOf(data.level as LogLevels) > -1) {
      this.sendToBackend([this.createLogPayload(data)])
    }
  }
}
